import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext, ThemeProvider } from 'styled-components';

import { dsDark, dsLight } from './theme';
import type { DsTheme } from './theme';
import LegacyStyles from './index';
import { EstilosDeRolagem } from './rolagem';

type NomeTema = 'light' | 'dark';

export const CHAVE_TEMA = 'painel-indicadores-tema';

type TemaContexto = {
  tema: NomeTema;
  alternarTema: () => void;
};

const Contexto = createContext<TemaContexto>({ tema: 'light', alternarTema: () => undefined });

function prefereEscuro(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function temaSalvo(): NomeTema {
  try {
    const salvo = localStorage.getItem(CHAVE_TEMA);

    if (salvo === 'light' || salvo === 'dark') return salvo;
  } catch (erro) {
    return 'light';
  }

  return prefereEscuro() ? 'dark' : 'light';
}

export function temaDs(tema: NomeTema): DsTheme {
  return tema === 'dark' ? dsDark : dsLight;
}

export function useTema(): TemaContexto {
  return useContext(Contexto);
}

/**
 * Os tokens do tema, com o claro como rede de segurança. O `TemaProvider`
 * envolve o app inteiro desde a raiz, então a rede só entra em teste, onde nem
 * todo componente é montado dentro de um provedor.
 */
export function useTokensDoTema(): DsTheme {
  const doContexto = useContext(ThemeContext) as DsTheme | undefined;

  return doContexto?.colors ? doContexto : dsLight;
}

export const TemaProvider: React.FC = ({ children }) => {
  const [tema, setTema] = useState<NomeTema>(temaSalvo);

  const alternarTema = useCallback(() => {
    setTema((atual) => (atual === 'dark' ? 'light' : 'dark'));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_TEMA, tema);
    } catch (erro) {
      // Sem persistência (modo privado, por exemplo) o tema vale só para esta sessão.
    }

    document.documentElement.setAttribute('data-tema', tema);
  }, [tema]);

  const valor = useMemo(() => ({ tema, alternarTema }), [tema, alternarTema]);

  return (
    <Contexto.Provider value={valor}>
      <ThemeProvider theme={temaDs(tema)}>
        <LegacyStyles theme={tema} />
        {/*
          Sem `theme`: ao contrário do estilo legado, que recebe o nome do tema
          como string e escolhe um bloco de variáveis, a rolagem lê os tokens do
          design system que vêm do provedor acima.
        */}
        <EstilosDeRolagem />
        {children}
      </ThemeProvider>
    </Contexto.Provider>
  );
};
